import { lazy, useState } from "react";
import { MessageCircle } from "lucide-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
  Link,
  ClientOnly,
} from "@tanstack/react-router";

import appCss from "../styles.css?url";
import { ThemeProvider } from "@/components/theme-provider";
import { Toaster } from "@/components/ui/sonner";
import { AppearanceProvider } from "@/components/appearance-provider";

// Lazy-load non-critical client widgets so they don't block initial JS parse / TTI.
const AutoTranslator = lazy(() =>
  import("@/components/site/auto-translator").then((m) => ({ default: m.AutoTranslator })),
);
const DeveloperInjector = lazy(() =>
  import("@/components/site/developer-injector").then((m) => ({ default: m.DeveloperInjector })),
);
const RouteSeoOverride = lazy(() =>
  import("@/components/site/route-seo-override").then((m) => ({ default: m.RouteSeoOverride })),
);

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-gradient">404</h1>
        <h2 className="mt-4 text-xl font-semibold">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <Link to="/" className="mt-6 inline-flex items-center justify-center rounded-md bg-gradient-brand px-4 py-2 text-sm font-medium text-white">
          Go home
        </Link>
      </div>
    </div>
  );
}

function parseErrorLocation(error: unknown): { file: string; line: string; col: string; raw: string } | null {
  const stack = (error as { stack?: string } | null)?.stack;
  if (!stack) return null;
  // Match the first frame that references a project source file.
  // Handles formats like "at Foo (/src/components/x.tsx:12:5)" and "at https://host/src/x.tsx:12:5".
  const re = /(?:\(|\s|^)((?:https?:\/\/[^\s)]+|file:\/\/[^\s)]+|\/[^\s):]+|[a-zA-Z]:[\\/][^\s):]+))(?::(\d+):(\d+))/g;
  let match: RegExpExecArray | null;
  let firstProject: { file: string; line: string; col: string; raw: string } | null = null;
  let firstAny: { file: string; line: string; col: string; raw: string } | null = null;
  while ((match = re.exec(stack)) !== null) {
    const full = match[1];
    const line = match[2];
    const col = match[3];
    const path = full.replace(/^https?:\/\/[^/]+/, "").replace(/^file:\/\//, "");
    const raw = `${path}:${line}:${col}`;
    if (!firstAny) firstAny = { file: path, line, col, raw };
    if (!firstProject && /\/src\//.test(path) && !/node_modules/.test(path)) {
      firstProject = { file: path, line, col, raw };
      break;
    }
  }
  return firstProject ?? firstAny;
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  const loc = parseErrorLocation(error);
  const message = error?.message || String(error);
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-12">
      <div className="w-full max-w-2xl">
        <div className="rounded-lg border border-border bg-card p-6 shadow-card">
          <h1 className="text-xl font-semibold">Something broke on this page</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            We hit an unexpected error while rendering. You can retry, or use the details below to track it down.
          </p>

          <div className="mt-4 rounded-md border border-destructive/30 bg-destructive/5 p-3 font-mono text-xs text-destructive break-words">
            {message}
          </div>

          {loc && (
            <div className="mt-3 rounded-md border border-border bg-muted/30 p-3 text-xs">
              <div className="text-muted-foreground">Origin</div>
              <div className="mt-1 font-mono break-all text-foreground">
                {loc.file}<span className="text-muted-foreground">:</span>{loc.line}<span className="text-muted-foreground">:</span>{loc.col}
              </div>
            </div>
          )}

          {error?.stack && (
            <details className="mt-3 rounded-md border border-border bg-muted/20 p-3 text-xs">
              <summary className="cursor-pointer select-none text-muted-foreground">Stack trace</summary>
              <pre className="mt-2 max-h-72 overflow-auto whitespace-pre-wrap font-mono text-[11px] leading-relaxed text-foreground/80">
                {error.stack}
              </pre>
            </details>
          )}

          <div className="mt-5 flex gap-2">
            <button
              onClick={() => { router.invalidate(); reset(); }}
              className="inline-flex items-center justify-center rounded-md bg-gradient-brand px-4 py-2 text-sm font-medium text-white"
            >
              Try again
            </button>
            <Link
              to="/"
              className="inline-flex items-center justify-center rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-muted/40"
            >
              Go home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "CoderLity — Digital Agency, SaaS, ERP, POS & Hosting" },
      { name: "description", content: "CoderLity builds premium web apps, SaaS, ERP, POS systems, runs digital marketing, and resells hosting & domains worldwide." },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
      { name: "googlebot", content: "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" },
      { property: "og:site_name", content: "CoderLity" },
      { property: "og:title", content: "CoderLity — Digital Agency Platform" },
      { property: "og:description", content: "Web, SaaS, ERP, POS, hosting and AI-powered growth — all in one premium digital partner." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://coderlity.com" },
      { property: "og:image", content: "https://coderlity.com/og-default.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "CoderLity — Digital Agency, SaaS, ERP, POS & Hosting" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: "https://coderlity.com/og-default.jpg" },
      { name: "twitter:title", content: "CoderLity — Digital Agency Platform" },
      { name: "twitter:description", content: "Web, SaaS, ERP, POS, hosting and AI-powered growth — all in one premium digital partner." },
      { name: "theme-color", content: "#4378BC", media: "(prefers-color-scheme: light)" },
      { name: "theme-color", content: "#0D1012", media: "(prefers-color-scheme: dark)" },
      { name: "format-detection", content: "telephone=no" },
    ],
    links: [
      { rel: "alternate", type: "text/markdown", href: "/llms.txt", title: "LLMs Context" },
      { rel: "sitemap", type: "application/xml", href: "/sitemap.xml" },
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      { rel: "manifest", href: "/site.webmanifest" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "preload",
        as: "style",
        href: "https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&family=Space+Grotesk:wght@600;700&display=swap",
      },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&family=Space+Grotesk:wght@600;700&display=swap",
        media: "print",
        onLoad: "this.media='all'",
      },
      { rel: "dns-prefetch", href: "https://qvqhffbveyekyzeiprfh.supabase.co" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "CoderLity",
          description:
            "Full-stack digital agency delivering web & mobile apps, SaaS, ERP, POS, AI automation, hosting, domains and marketing.",
          url: "https://coderlity.com",
          logo: "https://coderlity.com/favicon.svg",
          contactPoint: {
            "@type": "ContactPoint",
            contactType: "customer service",
            email: "coderlity@gmail.com",
            url: "https://coderlity.com/contact",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "CoderLity",
          url: "https://coderlity.com",
          potentialAction: {
            "@type": "SearchAction",
            target: "https://coderlity.com/search?q={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          name: "CoderLity",
          image: "https://coderlity.com/og-default.jpg",
          url: "https://coderlity.com",
          priceRange: "$$",
          description:
            "Full-stack digital agency delivering web & mobile apps, SaaS, ERP, POS, AI automation, hosting, domains and marketing.",
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" className="dark">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
      <AppearanceProvider>
        <ThemeProvider>
          <Outlet />
          <Toaster />
          <ClientOnly fallback={null}>
            <RouteSeoOverride />
          </ClientOnly>
          <ClientOnly fallback={null}>
            <WebVitalsReporter />
          </ClientOnly>
          <ClientOnly fallback={null}>
            <AiChatLauncher />
          </ClientOnly>
          <ClientOnly fallback={null}>
            <AutoTranslator />
          </ClientOnly>
          <ClientOnly fallback={null}>
            <DeveloperInjector />
          </ClientOnly>
        </ThemeProvider>
      </AppearanceProvider>
    </QueryClientProvider>
  );
}

function WebVitalsReporter() {
  if (typeof window !== "undefined") {
    // Fire once, non-blocking; the module lazy-loads `web-vitals`.
    import("@/lib/web-vitals").then((m) => m.startWebVitals()).catch(() => {});
  }
  return null;
}

function AiChatLauncher() {
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [ChatComponent, setChatComponent] = useState<React.ComponentType<{ defaultOpen?: boolean }> | null>(null);

  const handleOpen = () => {
    if (!ChatComponent) {
      import("@/components/ai-chat-widget").then((m) => {
        setChatComponent(() => m.AiChatWidget);
        setLoaded(true);
        setOpen(true);
      });
    } else {
      setOpen((o) => !o);
    }
  };

  if (ChatComponent && loaded) {
    return <ChatComponent defaultOpen={open} />;
  }

  return (
    <button
      type="button"
      onClick={handleOpen}
      aria-label="Open AI Live Chat"
      className="fixed bottom-4 right-4 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-brand text-white shadow-glow hover:scale-105 transition active:scale-95 cursor-pointer"
    >
      <MessageCircle className="h-6 w-6" />
    </button>
  );
}
